import { motion } from "framer-motion";
import CaseStudyLayout, { Bridge } from "./CaseStudyLayout";
import MediaFrame from "../components/MediaFrame";
import BrowserFrame from "../components/BrowserFrame";
import { projects } from "../lib/data";

const chapters = [
  { id: "cs-objectives", label: "Objectives", idx: 0 },
  { id: "cs-research", label: "Research", idx: 1 },
  { id: "cs-insights", label: "Insights", idx: 2 },
  { id: "cs-audience", label: "Audience", idx: 3 },
  { id: "cs-solutions", label: "Solutions", idx: 4 },
  { id: "cs-language", label: "Design Lang.", idx: 5 },
  { id: "cs-process", label: "Process", idx: 6 },
  { id: "cs-testing", label: "Testing", idx: 7 },
  { id: "cs-roadmap", label: "Roadmap", idx: 8 },
];

const Block = ({ id, idx, label, title, children, sticky }) => (
  <section
    id={id}
    data-chapter-idx={idx}
    className="py-16 md:py-28 border-t border-[color:var(--nn-border)] relative"
  >
    <div className="mx-auto max-w-[1600px] px-5 md:px-10 grid md:grid-cols-12 gap-8 md:gap-14">
      <div className="md:col-span-4">
        <div className={sticky ? "md:sticky md:top-32" : ""}>
          <div className="section-label mb-6">{label}</div>
          {title && (
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
              className="h-display text-4xl md:text-5xl lg:text-6xl leading-[0.9]"
            >
              {title}
            </motion.h2>
          )}
        </div>
      </div>
      <div className="md:col-span-8">{children}</div>
    </div>
  </section>
);

const Card = ({ tag, title, children, accent }) => (
  <motion.div
    initial={{ opacity: 0, y: 30 }}
    whileInView={{ opacity: 1, y: 0 }}
    viewport={{ once: true, margin: "-10% 0px" }}
    transition={{ duration: 0.7 }}
    className="p-6 md:p-7 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60 backdrop-blur relative overflow-hidden group"
  >
    {accent && <div className="absolute -top-8 -right-8 w-28 h-28 rounded-full bg-[color:var(--nn-accent)]/10 blur-3xl group-hover:bg-[color:var(--nn-accent)]/20 transition-colors" />}
    <div className="relative">
      {tag && <div className="font-mono text-[10px] text-[color:var(--nn-accent-text)] uppercase tracking-widest mb-3">{tag}</div>}
      <div className="font-display text-xl md:text-2xl mb-3">{title}</div>
      <div className="text-sm text-[color:var(--nn-muted)] leading-relaxed">{children}</div>
    </div>
  </motion.div>
);

const InterviewerExperience = () => {
  const project = projects.find((p) => p.slug === "interviewer-experience");

  return (
    <CaseStudyLayout project={project} chapters={chapters}>
      <Block id="cs-objectives" idx={0} label="(00) Objectives" sticky title={<>Principles that shaped <span className="font-serif-i text-[color:var(--nn-muted)]">every</span> decision.</>}>
        <div className="grid md:grid-cols-2 gap-5 mb-8">
          <Card tag="NORTH STAR" title="Verifiable · Monitored · Defensible" accent>Deliver interviews with an evidence trail — every session recorded, flagged, and reviewable.</Card>
          <Card tag="SECURITY" title="Audit-ready by default" accent>Defensible hiring decisions baked in — not a premium add-on.</Card>
          <Card tag="FAMILIARITY" title="No retraining" accent>Leverage existing Verity mental models — just an extended workflow.</Card>
          <Card tag="PANEL" title="Multi-interviewer ready" accent>Panel support built into every session from day one.</Card>
          <Card tag="EVIDENCE" title="Structured over subjective" accent>Flags, recordings, and structured feedback replace gut-feel assessments.</Card>
          <Card tag="ASYNC" title="Rate live or later" accent>Panelists can rate in-session or after the fact with full replay + flag timeline.</Card>
        </div>
      </Block>

      <Bridge label="how we researched" />

      <Block id="cs-research" idx={1} label="(01) Research" sticky title={<>Understanding the <span className="font-serif-i text-[color:var(--nn-muted)]">landscape.</span></>}>
        <div className="grid md:grid-cols-3 gap-4 mb-8">
          <Card tag="METHOD" title="Competitive Analysis">Benchmarked against WeCP to identify market gaps and differentiation opportunities.</Card>
          <Card tag="METHOD" title="Journey Mapping">Mapped the full interviewer lifecycle from scheduling through post-session evaluation.</Card>
          <Card tag="METHOD" title="User Interviews">Spoke with hiring managers and recruiters to uncover pain points in integrity and fragmentation.</Card>
        </div>

        <div className="rounded-2xl border border-[color:var(--nn-border)] overflow-hidden bg-[color:var(--nn-surface)]/40">
          <div className="grid grid-cols-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] p-4 border-b border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/70">
            <span>Capability</span><span className="text-center">WeCP</span><span className="text-center text-[color:var(--nn-accent-text)]">Verity</span>
          </div>
          {[
            ["Panel interviews", "●", "●"],
            ["Audio/video recording", "●", "●"],
            ["Real-time collaboration", "—", "●"],
            ["Structured evaluation", "●", "●"],
            ["Identity verification", "—", "●"],
            ["Real-time proctoring & flags", "—", "●"],
            ["Audit-ready evidence trail", "—", "●"],
            ["AI-assisted summaries", "—", "V2"],
          ].map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.04 }}
              className="grid grid-cols-3 items-center px-4 py-3 border-b border-[color:var(--nn-border)] last:border-b-0 text-sm hover:bg-[color:var(--nn-surface)]/70 transition-colors"
            >
              <span className="text-[color:var(--nn-text)]/90">{row[0]}</span>
              <span className="text-center text-[color:var(--nn-muted)]">{row[1]}</span>
              <span className={`text-center ${row[2] === "●" ? "text-[color:var(--nn-accent-text)]" : ""}`}>{row[2]}</span>
            </motion.div>
          ))}
        </div>

        <div className="mt-8">
          <MediaFrame
            src={project.assets.journey}
            ratio="16/9"
            caption="Admin Interview Journey Map — 6 phases from Schedule → Evaluate, with pain points + opportunities"
            testid="asset-journey"
            padded
            fit="contain"
          />
        </div>
      </Block>

      <Bridge label="the pivots" />

      <Block id="cs-insights" idx={2} label="(02) Insights" sticky title={<>Three quotes that <span className="font-serif-i text-[color:var(--nn-muted)]">rewrote</span> the brief.</>}>
        <div className="space-y-4">
          {[
            ["Remote interviews lack identity assurance and audit trails.", "Security and verification must be native — not optional add-ons."],
            ["Hiring teams need consistent, reviewable interview evidence.", "Recordings, flags, and structured feedback replace subjective notes."],
            ["Most tools prioritize convenience over integrity.", "Our differentiation: security, monitoring, and defensible outcomes."],
          ].map(([q, s], i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.08 }}
              className="grid md:grid-cols-12 gap-6 p-6 md:p-8 rounded-2xl border border-[color:var(--nn-border)]"
            >
              <div className="md:col-span-7 font-serif-i text-2xl leading-snug">"{q}"</div>
              <div className="md:col-span-5 border-t md:border-t-0 md:border-l border-[color:var(--nn-border)] md:pl-6 pt-4 md:pt-0">
                <div className="font-mono text-[10px] text-[color:var(--nn-accent-text)] mb-1">→ SO</div>
                <div className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">{s}</div>
              </div>
            </motion.div>
          ))}
        </div>
      </Block>

      <Bridge label="who we designed for" />

      <Block id="cs-audience" idx={3} label="(03) Audience" sticky title={<>Two personas. <span className="font-serif-i text-[color:var(--nn-muted)]">One shared</span> pain.</>}>
        <div className="grid md:grid-cols-2 gap-5">
          {[
            {
              tag: "PRIMARY", name: "Sarah", role: "Engineering Manager · Interviewer",
              needs: ["Verify candidate identity before starting", "Real-time behavioral flags without disruption", "Evidence-backed evaluation with recordings"],
              quote: "I need to trust who I'm talking to and have data to defend my hiring decisions.",
            },
            {
              tag: "SECONDARY", name: "Marcus", role: "Senior Recruiter · Admin",
              needs: ["Fast, consistent interview setup across teams", "Panel scheduling without calendar chaos", "Full audit trail for compliance reporting"],
              quote: "Setting up each interview shouldn't take 15 minutes and three different platforms.",
            },
          ].map((p) => (
            <motion.div
              key={p.name}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.7 }}
              className="p-6 md:p-8 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/60"
            >
              <div className="flex items-center justify-between mb-6">
                <span className="chip chip-accent">{p.tag}</span>
                <div className="font-mono text-[10px] text-[color:var(--nn-muted)]">Persona</div>
              </div>
              <div className="h-display text-4xl md:text-5xl">{p.name}</div>
              <div className="text-sm text-[color:var(--nn-muted)] mt-1 mb-6">{p.role}</div>
              <ul className="space-y-2 mb-6">
                {p.needs.map((n) => (
                  <li key={n} className="flex gap-3 text-sm">
                    <span className="text-[color:var(--nn-accent-text)]">→</span>
                    <span className="text-[color:var(--nn-text)]/85">{n}</span>
                  </li>
                ))}
              </ul>
              <blockquote className="font-serif-i text-lg md:text-xl border-l-2 border-[color:var(--nn-accent)] pl-4">"{p.quote}"</blockquote>
            </motion.div>
          ))}
        </div>
      </Block>

      <Bridge label="from insight to concept" />

      <Block id="cs-solutions" idx={4} label="(04) Solutions" sticky title={<>Four moves. <span className="font-serif-i text-[color:var(--nn-muted)]">One unified</span> platform.</>}>
        <div className="space-y-4 mb-10">
          {[
            { title: "Progressive Disclosure", tag: "Multi-step scheduling", why: "Single-page config confused recruiters with too many options at once.", what: "Create → add participants → configure security → schedule → send." },
            { title: "Non-intrusive Monitoring", tag: "Real-time flags", why: "Pop-up alerts disrupted live interviews and broke interviewer focus.", what: "Dedicated Live Events panel with timestamped flags — visible but never blocking." },
            { title: "Zero Context Switch", tag: "Collaboration suite", why: "Interviewers juggled Zoom, CodePen, and Miro with no shared context.", what: "Embedded code editor, whiteboard, and calculator in one session — shared state." },
            { title: "Async-friendly", tag: "Flexible rating", why: "Delayed raters lost context without recordings or behavioral data.", what: "Immediate or deferred rating with full replay, flag timeline, and structured feedback." },
          ].map((c, i) => (
            <motion.div
              key={c.title}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="p-6 md:p-7 rounded-2xl border border-[color:var(--nn-border)] grid md:grid-cols-12 gap-4 md:gap-8 group hover:border-[color:var(--nn-accent)]/40 transition-colors"
            >
              <div className="md:col-span-4">
                <div className="font-mono text-[10px] text-[color:var(--nn-accent-text)] mb-1">{c.tag}</div>
                <div className="font-display text-2xl">{c.title}</div>
              </div>
              <div className="md:col-span-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-2">Why</div>
                <div className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">{c.why}</div>
              </div>
              <div className="md:col-span-4">
                <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-accent-text)] mb-2">What</div>
                <div className="text-sm text-[color:var(--nn-text)]/85 leading-relaxed">{c.what}</div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <BrowserFrame
            src={project.assets.scheduling}
            alt="Scheduling wizard — Interview Details step"
            ratio="16/10"
            url="verity.talview.com/schedule"
            testid="asset-scheduling"
          />
          <BrowserFrame
            src={project.assets.liveEvents}
            alt="Live session with real-time flagged events"
            ratio="16/10"
            url="verity.talview.com/session/live"
            background="bg-gradient-to-br from-[#0d0d0d] via-[#1a1730] to-[#0d0d0d]"
            testid="asset-live-events"
          />
          <BrowserFrame
            src={project.assets.codeEditor}
            alt="Embedded code editor and whiteboard"
            ratio="16/10"
            url="verity.talview.com/session/collab"
            testid="asset-code-editor"
          />
          <BrowserFrame
            src={project.assets.rateCandidate}
            alt="Rate candidate — structured feedback"
            ratio="16/10"
            url="verity.talview.com/rate"
            background="bg-gradient-to-br from-[#0d0d0d] via-[#1f1a10] to-[#0d0d0d]"
            testid="asset-rate-candidate"
          />
        </div>
        <div className="mt-4 grid md:grid-cols-2 gap-2 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)]">
          <span><span className="text-[color:var(--nn-accent-text)]">▚</span> 4-step scheduling wizard (progressive disclosure)</span>
          <span><span className="text-[color:var(--nn-accent-text)]">▚</span> Live Events panel — non-intrusive flags</span>
          <span><span className="text-[color:var(--nn-accent-text)]">▚</span> Collaboration suite — code editor + whiteboard, shared state</span>
          <span><span className="text-[color:var(--nn-accent-text)]">▚</span> Rate Candidate — evidence-driven, structured feedback</span>
        </div>
      </Block>

      <Bridge label="the visual system" />

      <Block id="cs-language" idx={5} label="(05) Design Language" sticky title={<>The system behind <span className="font-serif-i text-[color:var(--nn-muted)]">every</span> screen.</>}>
        <div>
          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-4">Palette</div>
          <div className="grid grid-cols-3 md:grid-cols-6 gap-3 mb-10">
            {[
              { name: "Primary", hex: "#6366F1" },
              { name: "Accent", hex: "#2DD4BF" },
              { name: "Destructive", hex: "#EF4444" },
              { name: "Surface", hex: "#13131F" },
              { name: "Background", hex: "#0A0A12" },
              { name: "Foreground", hex: "#E8E8F0" },
            ].map((c, i) => (
              <motion.div
                key={c.name}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.05 }}
                className="rounded-xl overflow-hidden border border-[color:var(--nn-border)]"
              >
                <div className="h-16 md:h-20" style={{ background: c.hex }} />
                <div className="p-2 md:p-3">
                  <div className="text-[11px] md:text-xs">{c.name}</div>
                  <div className="font-mono text-[9px] md:text-[10px] text-[color:var(--nn-muted)]">{c.hex}</div>
                </div>
              </motion.div>
            ))}
          </div>

          <div className="font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] mb-4">Typography</div>
          <div className="space-y-3">
            {[
              { size: "80px", label: "DISPLAY · Satoshi", sample: "Interviewer Experience", cls: "text-3xl md:text-5xl" },
              { size: "48px", label: "HEADING · Satoshi", sample: "Section Title", cls: "text-2xl md:text-3xl" },
              { size: "26px", label: "SUBHEAD · Satoshi", sample: "Card Heading", cls: "text-lg md:text-xl" },
              { size: "16px", label: "BODY · Inter", sample: "Default paragraph text for descriptions and content blocks.", cls: "text-sm md:text-base font-sans" },
            ].map((t) => (
              <div key={t.label} className="p-4 rounded-xl border border-[color:var(--nn-border)]">
                <div className="flex items-center justify-between mb-2">
                  <div className="font-mono text-[10px] text-[color:var(--nn-muted)] uppercase tracking-widest">{t.label}</div>
                  <div className="font-mono text-[10px] text-[color:var(--nn-accent-text)]">{t.size}</div>
                </div>
                <div className={`${t.cls} font-display`}>{t.sample}</div>
              </div>
            ))}
          </div>
        </div>
      </Block>

      <Bridge label="how we shipped it" />

      <Block id="cs-process" idx={6} label="(06) Process" sticky title={<>How we <span className="font-serif-i text-[color:var(--nn-muted)]">got</span> there.</>}>
        <div className="relative">
          <div className="absolute left-[9px] md:left-[11px] top-2 bottom-2 w-[2px] bg-[color:var(--nn-border)]" aria-hidden />
          {[
            ["01", "Research & Benchmarking", "Analyzed WeCP and interviewed hiring managers to define baseline expectations."],
            ["02", "Information Architecture", "Mapped the full interviewer lifecycle from create to rate."],
            ["03", "Scheduling Wizard", "Evolved single-page config into a 4-step progressive disclosure flow."],
            ["04", "Hi-Fi Prototyping", "Built Figma prototypes for live monitoring, collab suite, and dual-path rating."],
            ["05", "Eng Collaboration", "Paired with devs on WebRTC, recording, and proctoring flag latency."],
          ].map(([n, h, p], i) => (
            <motion.div
              key={n}
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.6, delay: i * 0.06 }}
              className="relative pl-10 md:pl-14 pb-8"
            >
              <div className="absolute left-0 top-1 w-5 h-5 md:w-6 md:h-6 rounded-full bg-[color:var(--nn-accent)] grid place-items-center">
                <span className="font-mono text-[9px] text-[color:var(--nn-accent-ink)] font-bold">{n}</span>
              </div>
              <div className="font-display text-2xl md:text-3xl mb-1">{h}</div>
              <div className="text-sm text-[color:var(--nn-muted)] leading-relaxed">{p}</div>
            </motion.div>
          ))}
        </div>
      </Block>

      <Bridge label="what changed after testing" />

      <Block id="cs-testing" idx={7} label="(07) Testing" sticky title={<>What changed after <span className="font-serif-i text-[color:var(--nn-muted)]">user feedback.</span></>}>
        <div className="rounded-2xl border border-[color:var(--nn-border)] overflow-hidden">
          <div className="grid grid-cols-3 font-mono text-[10px] uppercase tracking-widest text-[color:var(--nn-muted)] p-4 border-b border-[color:var(--nn-border)] bg-[color:var(--nn-surface)]/70">
            <span>Before</span><span>After</span><span className="text-[color:var(--nn-accent-text)]">Outcome</span>
          </div>
          {[
            ["Pop-up alerts disrupted live interviews", "Dedicated Live Events panel", "Focus maintained"],
            ["Single-page config confused recruiters", "4-step wizard w/ progressive disclosure", "Setup errors gone"],
            ["No clear action for new users", "Emphasized CTA with onboarding", "Zero docs needed"],
            ["Rating lacked deferred path", "Dual-path rating with replay & flags", "Confident decisions"],
          ].map((row, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0 }}
              whileInView={{ opacity: 1 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, delay: i * 0.06 }}
              className="grid grid-cols-3 items-center px-4 py-4 border-b border-[color:var(--nn-border)] last:border-b-0 text-sm hover:bg-[color:var(--nn-surface)]/50 transition-colors"
            >
              <span className="text-[color:var(--nn-muted)] line-through">{row[0]}</span>
              <span className="text-[color:var(--nn-text)]">{row[1]}</span>
              <span className="text-[color:var(--nn-accent-text)] font-serif-i">{row[2]}</span>
            </motion.div>
          ))}
        </div>
      </Block>

      <Bridge label="what's next" />

      <Block id="cs-roadmap" idx={8} label="(08) Roadmap" sticky title={<>Shipped now — <span className="font-serif-i text-[color:var(--nn-muted)]">and next.</span></>}>
        <div className="grid md:grid-cols-2 gap-4">
          <div className="p-6 rounded-2xl border border-[color:var(--nn-border)] bg-[color:var(--nn-accent)]/5">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">V1 — Shipped</div>
              <span className="chip chip-accent">LIVE · MAR 26</span>
            </div>
            <ul className="space-y-2 text-sm">
              {["Scheduling wizard", "Live monitoring & flags", "Collaboration suite", "Rating & evidence", "Panel support", "Secure links & verification"].map((t) => (
                <li key={t} className="flex items-center gap-3"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-accent)]" />{t}</li>
              ))}
            </ul>
          </div>
          <div className="p-6 rounded-2xl border border-[color:var(--nn-border)]">
            <div className="flex items-center justify-between mb-4">
              <div className="font-display text-2xl">V2 — Planned</div>
              <span className="chip">NEXT 2Q</span>
            </div>
            <ul className="space-y-2 text-sm">
              {["AI-assisted summaries", "Interview templates", "Structured scorecards", "ATS / calendar integrations"].map((t) => (
                <li key={t} className="flex items-center gap-3 text-[color:var(--nn-muted)]"><span className="w-1.5 h-1.5 rounded-full bg-[color:var(--nn-muted)]" />{t}</li>
              ))}
            </ul>
          </div>
        </div>
        <blockquote className="mt-14 font-serif-i text-3xl md:text-5xl leading-tight max-w-4xl border-l-2 border-[color:var(--nn-accent)] pl-6">
          "Users can handle complexity when it's structured, contextual, and transparent."
        </blockquote>
      </Block>
    </CaseStudyLayout>
  );
};

export default InterviewerExperience;
